import React from 'react';
import { useAppTheme } from "../../theme/AppThemeContext.jsx";
import { Box, Typography, Paper, ButtonBase } from '@mui/material';

// ---------- Helper Functions ----------
function toMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function toAmPm(hhmm) {
  const [hRaw, m] = hhmm.split(":").map(Number);
  const am = hRaw < 12;
  const h12 = ((hRaw + 11) % 12) + 1;
  const mm = String(m).padStart(2, "0");
  return `${h12}:${mm} ${am ? "AM" : "PM"}`;
}

function diffMinutes(startHHMM, endHHMM) {
  if (!startHHMM || !endHHMM) return null;
  const d = toMinutes(endHHMM) - toMinutes(startHHMM);
  return Number.isFinite(d) && d > 0 ? d : null;
}

function formatTimeRange(task) {
  if (!task.startTime) return "";
  const start = toAmPm(task.startTime);

  // If end time exists, include duration like screenshot
  if (task.endTime) {
    const end = toAmPm(task.endTime);
    const mins = diffMinutes(task.startTime, task.endTime);
    const dur = mins ? ` (${mins} min)` : "";
    return `${start} – ${end}${dur}`;
  }

  return start;
}

function renderTaskIcon(task) {
  // Placeholder only: NO text, NO font dependency.
  return (
    <Box
      sx={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        bgcolor: "#fff",
        opacity: 0.95,
      }}
    />
  );
}

function computeVisualTops(sortedTasks, pxPerMin, startMin, opts) {
  const MIN_GAP_PX = opts?.minGapPx ?? 10;     // space between back-to-back icons
  const MAX_GAP_PX = opts?.maxGapPx ?? 220;    // compress very large gaps (optional)
  const COMPRESS_EMPTY = opts?.compressEmpty ?? true;
  const ALIGN_TO_GUTTER = opts?.alignToGutter ?? true; // New option for gutter alignment
  const BACK_TO_BACK_GAP = opts?.backToBackGap ?? 16; // Enhanced gap for immediate back-to-back tasks
  const MAX_DEVIATION_PX = opts?.maxDeviationPx ?? 8; // Maximum deviation from ideal position

  let prevIdeal = null;
  let prevVisual = null;

  return sortedTasks.map((t) => {
    const sMin = t.startTime ? toMinutes(t.startTime) : startMin;
    const idealTop = (sMin - startMin) * pxPerMin;

    let visualTop = idealTop;
    let idealDelta = 0; // Define idealDelta here with default value
    let isBackToBack = false;

    if (prevIdeal != null && prevVisual != null) {
      idealDelta = idealTop - prevIdeal;
      
      // Check if this is a back-to-back task (same or very close start/end times)
      const prevTaskIndex = sortedTasks.indexOf(t) - 1;
      const prevTask = prevTaskIndex >= 0 ? sortedTasks[prevTaskIndex] : null;
      
      if (prevTask && prevTask.endTime) {
        const prevEndMin = toMinutes(prevTask.endTime);
        isBackToBack = Math.abs(sMin - prevEndMin) <= 5; // Within 5 minutes = back-to-back
      }

      // 1) optional compression of very large empty gaps
      if (COMPRESS_EMPTY && idealDelta > MAX_GAP_PX) {
        visualTop = prevVisual + MAX_GAP_PX;
      }

      // 2) enforce minimum separation - use enhanced gap for back-to-back tasks
      const requiredGap = isBackToBack ? BACK_TO_BACK_GAP : MIN_GAP_PX;
      if (visualTop < prevVisual + requiredGap) {
        visualTop = prevVisual + requiredGap;
      }
    }

    // 3) Ensure tight alignment with time gutter (fair representation)
    if (ALIGN_TO_GUTTER && visualTop !== idealTop && idealDelta > 0) {
      // Much stricter alignment - limit deviation to maintain gutter correspondence
      const maxDeviation = isBackToBack ? 
        Math.min(MAX_DEVIATION_PX, BACK_TO_BACK_GAP / 2) : // Small deviation for back-to-back
        Math.min(MAX_DEVIATION_PX, MIN_GAP_PX / 2);      // Even smaller for regular tasks
      
      // Ensure we don't deviate too far from the ideal position
      const deviation = Math.abs(visualTop - idealTop);
      if (deviation > maxDeviation) {
        visualTop = idealTop + (visualTop > idealTop ? maxDeviation : -maxDeviation);
      }
    }

    prevIdeal = idealTop;
    prevVisual = visualTop;

    return { ...t, _top: visualTop, _idealTop: idealTop, _isBackToBack: isBackToBack };
  });
}

// ---------- Date Strip Component (matching screenshot) ----------
function MultiDayDateStrip({ dateRange, selectedDate, onDateSelect }) {
  const { tokens } = useAppTheme();

  const toDayKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const isSameDay = (date1, date2) => {
    return toDayKey(date1) === toDayKey(date2);
  };

  return (
    <Box
      sx={{
        px: { xs: 2, md: 4 },
        py: 1,
        display: "flex",
        justifyContent: "space-between",
        gap: 1,
        background: "rgb(248, 248, 248)",
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        overflowX: "auto",
      }}
    >
      {dateRange.map((date) => {
        const isSelected = isSameDay(date, selectedDate);
        const dow = date.toLocaleString(undefined, { weekday: "short" });
        const dayNum = date.getDate();

        return (
          <ButtonBase
            key={toDayKey(date)}
            onClick={() => onDateSelect(date)}
            sx={{
              minWidth: 90,
              px: 1,
              py: 0.75,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
              userSelect: "none",
              transition: "background-color 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.04)",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: "rgba(0,0,0,0.40)",
              }}
            >
              {dow}
            </Typography>

            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: isSelected ? "rgba(0,0,0,0.85)" : "transparent",
                color: isSelected ? "#fff" : "rgba(0,0,0,0.55)",
                display: "grid",
                placeItems: "center",
                fontWeight: 900,
                fontSize: 12,
                border: isSelected ? `1px solid rgba(0,0,0,0.85)` : "1px solid transparent",
                transition: "all 0.2s ease-in-out",
              }}
            >
              {dayNum}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// ---------- Time Gutter Component ----------
function TimeGutter({ startMin, endMin, pxPerMin, visualTops }) {
  
  // Transform visualTops into anchor format expected by the reference code
  const visualAnchors = React.useMemo(() => {
    if (!visualTops || visualTops.length === 0) {
      return [];
    }
    
    const anchors = [];
    const processedTimes = new Set();
    
    visualTops.forEach(task => {
      // Process start time
      if (task.startTime) {
        const startMin = toMinutes(task.startTime);
        const startKey = `start-${startMin}-${task.id}`;
        
        if (!processedTimes.has(startKey)) {
          const h12 = ((Math.floor(startMin / 60) + 11) % 12) + 1;
          const mm = String(startMin % 60).padStart(2, "0");
          const am = Math.floor(startMin / 60) < 12;
          
          anchors.push({
            id: `${task.id}-start`,
            yTop: task._top || 0,
            yBottom: task._top || 0, // Same position for start time
            startLabel: `${h12}:${mm} ${am ? 'AM' : 'PM'}`,
            endLabel: `${h12}:${mm} ${am ? 'AM' : 'PM'}`,
          });
          processedTimes.add(startKey);
        }
      }
      
      // Process end time
      if (task.endTime) {
        const endMin = toMinutes(task.endTime);
        const endKey = `end-${endMin}-${task.id}`;
        
        if (!processedTimes.has(endKey)) {
          const h12 = ((Math.floor(endMin / 60) + 11) % 12) + 1;
          const mm = String(endMin % 60).padStart(2, "0");
          const am = Math.floor(endMin / 60) < 12;
          
          // Calculate task height - use default if not available
          const taskHeight = task._height || 40;
          const bottomPosition = (task._top || 0) + taskHeight;
          
          anchors.push({
            id: `${task.id}-end`,
            yTop: bottomPosition, // Position at bottom edge
            yBottom: bottomPosition, // Same position for end time
            startLabel: `${h12}:${mm} ${am ? 'AM' : 'PM'}`,
            endLabel: `${h12}:${mm} ${am ? 'AM' : 'PM'}`,
          });
          processedTimes.add(endKey);
        }
      }
    });
    
    // Sort by visual position
    return anchors.sort((a, b) => a.yTop - b.yTop);
  }, [visualTops]);

  return (
    <Box sx={{ position: "relative", height: (endMin - startMin) * pxPerMin }}>
      {visualAnchors.map((a) => (
        <React.Fragment key={a.id}>
          {/* start time at TOP edge of card */}
          <Typography
            sx={{
              position: "absolute",
              top: a.yTop,
              left: 12,
              fontSize: 12,
              color: "rgba(0,0,0,0.45)",
              transform: "translateY(-2px)", // small optical tweak
            }}
          >
            {a.startLabel}
          </Typography>

          {/* end time at BOTTOM edge of card */}
          <Typography
            sx={{
              position: "absolute",
              top: a.yBottom,
              left: 12,
              fontSize: 12,
              color: "rgba(0,0,0,0.45)",
              transform: "translateY(-14px)", // pull up so text sits inside bottom edge
            }}
          >
            {a.endLabel}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  );
}

// ---------- Timeline Node Component (DROP-IN REPLACEMENT) ----------
function TimelineNode({ task, spineX, startMin, pxPerMin, onTaskClick }) {
  const { tokens } = useAppTheme();
  // Use precomputed _top from visual layout pass
  const top = typeof task._top === "number"
    ? task._top
    : ((task.startTime ? toMinutes(task.startTime) : startMin) - startMin) * pxPerMin;


  const timeLine = formatTimeRange(task); // now includes (xx min)
  const title = task.title ?? "";

  // Dynamic shape based on task duration
  const duration =
    task.startTime && task.endTime
      ? diffMinutes(task.startTime, task.endTime)
      : 0;

  const isShort = duration < 30;

  return (
    <ButtonBase
      onClick={() => {
        onTaskClick?.(task);
      }}
      sx={{
        position: "absolute",
        top,
        left: 0,
        width: "100%",
        justifyContent: "flex-start",
        textAlign: "left",
        borderRadius: 2,
        py: 0.25,
        "&:hover .nodeBubble": {
          transform: "scale(1.02)",
          boxShadow: "0 3px 14px rgba(0,0,0,0.14)",
        },
        "&:hover .nodeTitle": {
          textDecoration: "underline",
          textDecorationColor: "rgba(0,0,0,0.25)",
        },
      }}
    >
      {/* Icon bubble with dynamic shape */}
      <Box
        className="nodeBubble"
        sx={{
          position: "absolute",
          left: spineX - (isShort ? 20 : 14),
          width: isShort ? 40 : 28,
          height: isShort ? 40 : Math.max(duration * pxPerMin, 40),
          borderRadius: isShort ? "50%" : 14,
          bgcolor: tokens.primary,
          display: "grid",
          placeItems: "center",
          boxShadow: "0 2px 10px rgba(0,0,0,0.10)",
          transition: "transform 120ms ease, box-shadow 120ms ease",
        }}
      >
        {renderTaskIcon(task)}
      </Box>

      {/* Text block */}
      <Box sx={{ ml: 9, pr: 2 }}>
        <Typography
          sx={{
            fontSize: 12,
            lineHeight: "16px",
            color: "rgba(0,0,0,0.55)",
            whiteSpace: "nowrap",
          }}
        >
          {timeLine}
        </Typography>

        <Typography
          className="nodeTitle"
          sx={{
            fontSize: 14,
            lineHeight: "18px",
            fontWeight: 700,
            color: "rgba(0,0,0,0.88)",
            mt: 0.2,
          }}
        >
          {title}
        </Typography>
      </Box>
    </ButtonBase>
  );
}

// ---------- Day Column Component (DROP-IN REPLACEMENT) ----------
function DayColumn({ tasks, startMin, endMin, pxPerMin, onTaskClick }) {
  const { tokens } = useAppTheme();
  const height = (endMin - startMin) * pxPerMin;
  const spineX = 28;


  // Sort tasks by start time so connectors are correct
  const sorted = React.useMemo(() => {
    return [...tasks ?? []].sort((a, b) => {
      const am = a.startTime ? toMinutes(a.startTime) : startMin;
      const bm = b.startTime ? toMinutes(b.startTime) : startMin;
      return am - bm;
    });
  }, [tasks, startMin]);

  // Apply visual layout pass
  const laidOut = React.useMemo(() => {
    return computeVisualTops(sorted, pxPerMin, startMin, {
      minGapPx: 10,              // Standard minimum gap
      maxGapPx: 200,             // Maximum gap compression
      compressEmpty: true,       // Compress large empty spaces
      alignToGutter: true,       // Ensure fair time representation
      backToBackGap: 16,         // Enhanced gap for back-to-back tasks
      maxDeviationPx: 8,         // Maximum deviation from ideal position
    });
  }, [sorted, pxPerMin, startMin]);

  // Convert each node to a Y coordinate (center of bubble)
  const nodeYs = React.useMemo(() => {
    return laidOut.map((t) => {
      return t._top + 20; // bubble center (40px bubble -> +20)
    });
  }, [laidOut]);

  return (
    <Box sx={{ position: "relative", height, px: 2 }}>
      {/* main spine */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: spineX,
          width: 2,
          bgcolor: tokens.primaryHover,
          borderRadius: 2,
          opacity: 0.9,
        }}
      />

      {/* connector segments between nodes (gives the screenshot "bead chain" feel) */}
      {nodeYs.map((y, i) => {
        if (i === nodeYs.length - 1) return null;
        const nextY = nodeYs[i + 1];
        const segTop = Math.min(y, nextY) + 22; // start below current bubble
        const segHeight = Math.max(0, Math.abs(nextY - y) - 44); // leave space for bubbles

        if (segHeight <= 2) return null;

        return (
          <Box
            key={`seg-${i}`}
            sx={{
              position: "absolute",
              left: spineX,
              top: segTop,
              width: 2,
              height: segHeight,
              bgcolor: tokens.primaryHover,
              borderRadius: 2,
              opacity: 0.9,
            }}
          />
        );
      })}

      {laidOut.map((task) => (
        <TimelineNode
          key={task.id}
          task={task}
          spineX={spineX}
          startMin={startMin}
          pxPerMin={pxPerMin}
          onTaskClick={onTaskClick}
        />
      ))}
    </Box>
  );
}

// ---------- Main MultiDayView Component ----------
function MultiDayView({
  user,
  startDate,
  endDate,
  spriteUrl,
  tasks,
  onTaskClick,
  onTasksLoaded,
}) {
  const [tasksByDate, setTasksByDate] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [selectedDate, setSelectedDate] = React.useState(startDate || new Date());

  // Smart time scale based on all tasks across all days
  const allTasks = React.useMemo(
    () => Object.values(tasksByDate ?? {}).flatMap(dateData => dateData?.tasks || []),
    [tasksByDate]
  );

  const [startMin, endMin] = React.useMemo(() => {
    if (!allTasks.length) {
      return [6 * 60, 18 * 60]; // fallback 6AM–6PM
    }

    // Find earliest start time and latest end time across all tasks
    const startTimes = allTasks
      .map(t => t.startTime && toMinutes(t.startTime))
      .filter(Boolean);

    const endTimes = allTasks
      .map(t => t.endTime && toMinutes(t.endTime))
      .filter(Boolean);

    if (startTimes.length === 0 || endTimes.length === 0) {
      return [6 * 60, 18 * 60]; // fallback if no valid times
    }

    const earliestStart = Math.min(...startTimes);
    const latestEnd = Math.max(...endTimes);

    // Round to nearest hour boundaries as requested
    const roundedStart = Math.floor(earliestStart / 60) * 60;
    const roundedEnd = Math.ceil(latestEnd / 60) * 60;

    return [roundedStart, roundedEnd];
  }, [allTasks]);

  const pxPerMin = 2.2;  // slightly tighter than 2.4
  const gridHeight = (endMin - startMin) * pxPerMin;

  // Calculate date range (default to 4 days for Multi-Day view)
  const dateRange = React.useMemo(() => {
    const dates = [];
    const start = startDate || new Date();
    const days = endDate ? Math.ceil((endDate - start) / (1000 * 60 * 60 * 24)) + 1 : 4;
    
    for (let i = 0; i < days; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [startDate, endDate]);

  // Load tasks for each date in the range
  React.useEffect(() => {
    if (!user) {
      setTasksByDate({});
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Group tasks by date from the tasks prop
    const tasksMap = {};
    let hasError = false;
    let totalTasksFound = 0;
    
    for (const date of dateRange) {
      try {
        const dateKey = date.toISOString().split('T')[0];
        // Filter tasks for this specific date
        const dateTasks = tasks ? tasks.filter(task => {
          if (!task.dateString) return false;
          return task.dateString === dateKey;
        }) : [];
        
        totalTasksFound += dateTasks.length;
        tasksMap[dateKey] = {
          tasks: dateTasks,
          loading: false,
          error: null
        };
      } catch (err) {
        console.error(`MultiDayView: Error processing date ${date.toISOString().split('T')[0]}:`, err);
        const dateKey = date.toISOString().split('T')[0];
        tasksMap[dateKey] = {
          tasks: [],
          loading: false,
          error: err.message
        };
        hasError = true;
      }
    }

    setTasksByDate(tasksMap);
    setError(hasError ? 'Some dates failed to load' : null);
    setLoading(false);
    
    // Notify parent of total tasks loaded
    onTasksLoaded?.(totalTasksFound);
  }, [user, dateRange, tasks, onTasksLoaded]);

  // Prepare tasks by day for the new layout
  const tasksByDay = React.useMemo(() => {
    const result = {};
    dateRange.forEach(date => {
      const dateKey = date.toISOString().split('T')[0];
      const dateData = tasksByDate[dateKey];
      result[dateKey] = dateData ? dateData.tasks : [];
    });
    return result;
  }, [dateRange, tasksByDate]);

  // Create a combined visual layout for the TimeGutter
  const allVisualTasks = React.useMemo(() => {
    const allTasks = [];
    dateRange.forEach(date => {
      const dateKey = date.toISOString().split('T')[0];
      const tasks = tasksByDay[dateKey] || [];
      const sorted = [...tasks].sort((a, b) => {
        const am = a.startTime ? toMinutes(a.startTime) : startMin;
        const bm = b.startTime ? toMinutes(b.startTime) : startMin;
        return am - bm;
      });
      const laidOut = computeVisualTops(sorted, pxPerMin, startMin, {
        minGapPx: 10,              // Standard minimum gap
        maxGapPx: 200,             // Maximum gap compression
        compressEmpty: true,       // Compress large empty spaces
        alignToGutter: true,       // Ensure fair time representation
        backToBackGap: 16,         // Enhanced gap for back-to-back tasks
        maxDeviationPx: 8,         // Maximum deviation from ideal position
      });
      allTasks.push(...laidOut);
    });
    return allTasks.sort((a, b) => (a._top || 0) - (b._top || 0));
  }, [dateRange, tasksByDay, startMin, pxPerMin]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <Typography sx={{ color: 'text.secondary' }}>Loading multi-day view...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <Typography sx={{ color: 'error' }}>{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Date Strip at the top (matching screenshot) */}
      <MultiDayDateStrip 
        dateRange={dateRange}
        selectedDate={selectedDate}
        onDateSelect={setSelectedDate}
      />

      {/* Main Grid Layout */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "80px 1fr",
          width: "100%",
          mt: 2,
          alignItems: "start",
        }}
      >
        {/* Shared timeline height wrapper */}
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "grid",
            gridTemplateColumns: "80px 1fr",
            position: "relative",
            height: gridHeight,   // 🔥 critical fix
          }}
        >
          {/* Left time gutter */}
          <Box sx={{ height: gridHeight }}>
            <TimeGutter
              startMin={startMin}
              endMin={endMin}
              pxPerMin={pxPerMin}
              visualTops={allVisualTasks}
            />
          </Box>

          {/* Right: N day columns */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${dateRange.length}, minmax(0,1fr))`,
              columnGap: 32,
              height: gridHeight,     // 🔥 critical fix
              position: "relative",
            }}
          >
            {dateRange.map((date) => {
              const dateKey = date.toISOString().split("T")[0];

              return (
                <DayColumn
                  key={dateKey}
                  date={date}
                  tasks={tasksByDay[dateKey] ?? []}
                  startMin={startMin}
                  endMin={endMin}
                  pxPerMin={pxPerMin}
                  onTaskClick={onTaskClick}
                />
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default MultiDayView;
